import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Search, Check, X } from "lucide-react";

interface Props {
  email: string;
  onLinked: () => void;
  onCancel: () => void;
}

export default function ArtistLinking({ email, onLinked, onCancel }: Props) {
  const [searchName, setSearchName] = useState("");
  const [searched, setSearched] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");

  const searchResults = useQuery(
    api.queries.searchArtistsByName,
    searched ? { name: searchName } : "skip",
  );

  const linkProfile = useMutation(api.mutations.linkArtistProfile);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!searchName.trim()) return;
    setSearched(true);
  }

  async function handleLink(artistId: string) {
    setError("");
    setLinking(true);
    try {
      await linkProfile({ artistId: artistId as any });
      onLinked();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to link profile");
    } finally {
      setLinking(false);
    }
  }

  return (
    <div className="artist-linking-overlay">
      <div className="artist-linking-card">
        <div className="artist-linking-header">
          <h2>Link Your Artist Profile</h2>
          <button className="modal-close" onClick={onCancel} type="button">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="artist-linking-intro">
          Welcome, {email}! Search for your artist profile to link it to your account.
        </p>

        <form className="artist-linking-search" onSubmit={handleSearch}>
          <input
            className="theme-input"
            onChange={(e) => setSearchName(e.target.value)}
            placeholder="Enter your artist or stage name..."
            value={searchName}
          />
          <button className="primary-action" disabled={!searchName.trim()} type="submit">
            <Search className="h-4 w-4" />
            Search
          </button>
        </form>

        {searched && searchResults ? (
          <div className="artist-linking-results">
            {searchResults.length > 0 ? (
              <>
                <p className="results-label">
                  {searchResults.length} match{searchResults.length === 1 ? "" : "es"} found. Is one of these you?
                </p>
                {searchResults.map((artist) => (
                  <div className="linking-result-row" key={artist.id}>
                    <div>
                      <strong>{artist.name}</strong>
                      {artist.stageName ? <span> — {artist.stageName}</span> : null}
                      {artist.userId ? (
                        <small className="already-linked">Already linked</small>
                      ) : null}
                    </div>
                    <button
                      className="primary-action"
                      disabled={linking || !!artist.userId}
                      onClick={() => handleLink(artist.id)}
                      type="button"
                    >
                      <Check className="h-4 w-4" />
                      {artist.userId ? "Taken" : "That's me"}
                    </button>
                  </div>
                ))}
              </>
            ) : (
              <p className="empty-state">
                No artists found matching "{searchName}". Ask your producer to add your profile first.
              </p>
            )}
          </div>
        ) : null}

        {error ? <p className="form-error">{error}</p> : null}
      </div>
    </div>
  );
}